import {
  applyServiceWorkerUpdate,
  checkForServiceWorkerUpdate,
  hasWaitingServiceWorkerUpdate,
  onServiceWorkerUpdateReady,
} from '../serviceWorkerRegistration'

export type AppUpdateState = 'idle' | 'checking' | 'update-ready' | 'applying'
type AppUpdateSource = 'service-worker' | 'app-version' | 'asset-manifest'
type AppVersionFile = {
  version?: string
  build_id?: string
  commit_sha?: string
  built_at?: string
}

const CHECK_THROTTLE_MS = 60_000
const PERIODIC_CHECK_MS = 5 * 60_000
const APP_VERSION_STORAGE_KEY = 'proka_app_build_id'

let state: AppUpdateState = 'idle'
const listeners = new Set<() => void>()
let lastCheckAt = 0
let pendingUserReload = false
let reloadDone = false
let initialized = false
let updateSource: AppUpdateSource | null = null
let pendingAppVersionKey: string | null = null

function emit(): void {
  listeners.forEach((listener) => listener())
}

function setState(next: AppUpdateState): void {
  if (state === next) {
    return
  }
  state = next
  emit()
}

function normalizePath(value: string | undefined): string | null {
  if (!value) {
    return null
  }

  try {
    return new URL(value, window.location.origin).pathname
  } catch {
    return value.startsWith('/') ? value : `/${value}`
  }
}

function appVersionKey(version: AppVersionFile): string | null {
  return version.build_id || version.commit_sha || version.version || null
}

function readStoredAppVersionKey(): string | null {
  try {
    return window.localStorage.getItem(APP_VERSION_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStoredAppVersionKey(versionKey: string): void {
  try {
    window.localStorage.setItem(APP_VERSION_STORAGE_KEY, versionKey)
  } catch {
    // If storage is unavailable, reload still applies the new bundle.
  }
}

async function fetchAppVersion(): Promise<AppVersionFile | null> {
  const versionUrl = `${process.env.PUBLIC_URL || ''}/app-version.json?ts=${Date.now()}`
  const response = await fetch(versionUrl, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
    },
  })

  if (!response.ok) {
    return null
  }

  return await response.json() as AppVersionFile
}

async function fetchAppVersionKey(): Promise<string | null> {
  const version = await fetchAppVersion()

  return version ? appVersionKey(version) : null
}

async function hasAppVersionUpdate(): Promise<boolean> {
  const nextVersionKey = await fetchAppVersionKey()
  if (!nextVersionKey) {
    return false
  }

  const currentVersionKey = readStoredAppVersionKey()
  if (!currentVersionKey) {
    writeStoredAppVersionKey(nextVersionKey)
    return false
  }

  if (currentVersionKey === nextVersionKey) {
    return false
  }

  pendingAppVersionKey = nextVersionKey
  return true
}

function currentScriptPath(): string | null {
  const script = Array.from(document.scripts).find((item) => {
    return item.src.includes('/static/js/main.')
  })

  return normalizePath(script?.src)
}

function currentStylePath(): string | null {
  const link = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')).find((item) => {
    return item.href.includes('/static/css/main.')
  })

  return normalizePath(link?.href)
}

async function hasAssetManifestUpdate(): Promise<boolean> {
  const manifestUrl = `${process.env.PUBLIC_URL || ''}/asset-manifest.json?ts=${Date.now()}`
  const response = await fetch(manifestUrl, {
    cache: 'no-store',
    headers: {
      'Cache-Control': 'no-cache',
    },
  })

  if (!response.ok) {
    return false
  }

  const manifest = await response.json() as {
    files?: {
      'main.js'?: string
      'main.css'?: string
    }
  }
  const manifestScriptPath = normalizePath(manifest.files?.['main.js'])
  const manifestStylePath = normalizePath(manifest.files?.['main.css'])
  const scriptPath = currentScriptPath()
  const stylePath = currentStylePath()

  return Boolean(
    (manifestScriptPath && scriptPath && manifestScriptPath !== scriptPath)
    || (manifestStylePath && stylePath && manifestStylePath !== stylePath),
  )
}

export function getAppUpdateState(): AppUpdateState {
  return state
}

export function subscribeAppUpdate(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export async function checkForAppUpdate(options?: { force?: boolean }): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return
  }

  if (state === 'update-ready' || state === 'applying') {
    return
  }

  const now = Date.now()
  if (!options?.force && now - lastCheckAt < CHECK_THROTTLE_MS) {
    return
  }

  lastCheckAt = now
  setState('checking')

  try {
    await checkForServiceWorkerUpdate()
    if (hasWaitingServiceWorkerUpdate()) {
      pendingAppVersionKey = await fetchAppVersionKey()
      updateSource = 'service-worker'
      setState('update-ready')
    } else if (await hasAppVersionUpdate()) {
      updateSource = 'app-version'
      setState('update-ready')
    } else if (await hasAssetManifestUpdate()) {
      updateSource = 'asset-manifest'
      setState('update-ready')
    } else if (state === 'checking') {
      updateSource = null
      setState('idle')
    }
  } catch {
    if (state === 'checking') {
      setState('idle')
    }
  }
}

export function applyAppUpdate(): void {
  if (state !== 'update-ready' && state !== 'applying') {
    return
  }

  if (!hasWaitingServiceWorkerUpdate()) {
    if (updateSource === 'app-version' || updateSource === 'asset-manifest') {
      if (pendingAppVersionKey) {
        writeStoredAppVersionKey(pendingAppVersionKey)
      }
      setState('applying')
      window.location.reload()
    }
    return
  }

  setState('applying')
  pendingUserReload = true
  if (pendingAppVersionKey) {
    writeStoredAppVersionKey(pendingAppVersionKey)
  }

  const sent = applyServiceWorkerUpdate()
  if (!sent) {
    pendingUserReload = false
    setState('idle')
  }
}

export function initServiceWorkerUpdateCoordinator(): () => void {
  if (!('serviceWorker' in navigator)) {
    return () => undefined
  }

  if (initialized) {
    return () => undefined
  }
  initialized = true

  const unsubscribeUpdateReady = onServiceWorkerUpdateReady(() => {
    updateSource = 'service-worker'
    void fetchAppVersionKey()
      .then((versionKey) => {
        pendingAppVersionKey = versionKey
      })
      .catch(() => {
        pendingAppVersionKey = null
      })
    setState('update-ready')
  })

  const onControllerChange = () => {
    if (!pendingUserReload || reloadDone) {
      return
    }
    reloadDone = true
    window.location.reload()
  }

  const onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      return
    }
    void checkForAppUpdate()
  }

  const onFocus = () => {
    void checkForAppUpdate()
  }

  navigator.serviceWorker.addEventListener('controllerchange', onControllerChange)
  document.addEventListener('visibilitychange', onVisibilityChange)
  window.addEventListener('focus', onFocus)
  const intervalId = window.setInterval(() => {
    if (document.visibilityState === 'visible') {
      void checkForAppUpdate()
    }
  }, PERIODIC_CHECK_MS)

  void checkForAppUpdate({ force: true })

  return () => {
    unsubscribeUpdateReady()
    navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    window.removeEventListener('focus', onFocus)
    window.clearInterval(intervalId)
    initialized = false
  }
}
